import React, { useState, useCallback, useEffect } from 'react'
import { URL_THUMBOR } from 'constants/urls'
import { CURRENT_AWARENESS_ARTICLE } from 'constants/controllers'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import url from 'libraries/front-end-core/core/url'
import ArrowLink from 'components/redesign/ArrowLink'
import cx from 'classnames'
import Route from 'route-parser'
import { pushUrl } from 'libraries/front-end-core/core/history'
import { isLink } from 'helpers/dom'
import Skeleton from '@mui/material/Skeleton'
import { htmlText } from 'helpers/htmlText'
import { parseHtmlContent } from 'helpers/parseHtmlContent'
import { useDispatch, useSelector } from 'react-redux'
import { setSignUpModalIsOpen, setUserRolesModalIsOpen } from 'redux/actions'
import selectUserAccessRoles from 'redux/selectors/selectAccessRoles'
import useOverflowDetection from 'hooks/useOverflowDetection'

function pushUrlIfNotLink (URL) {
  return e => {
    if (!isLink(e.target)) {
      pushUrl(URL)
    }
  }
}

export function useHandleArticleClick () {
  const dispatch = useDispatch()
  const accessRoles = useSelector(selectUserAccessRoles)

  return useCallback(
    (isLogged, URL, aol) => (e) => {
      if (!isLogged) {
        dispatch(setSignUpModalIsOpen(true))
      } else {
        const hasNewsAccess = accessRoles.some(role => role.startsWith(`${aol}_news_`))
        if (!hasNewsAccess) {
          dispatch(setUserRolesModalIsOpen(true))
        } else {
          pushUrlIfNotLink(URL)(e)
        }
      }
    },
    [dispatch, accessRoles]
  )
}

const thumborRoute = new Route(URL_THUMBOR)

const ReadFullStoryLink = ({ path, tileHover, aol, isLogged, onClick }) => {
  const urlParams = { aol, id: path[1] }

  return (
    <div className='ml3' onClick={onClick}>
      <ArrowLink
        text='Read full story'
        url={isLogged ? url(CURRENT_AWARENESS_ARTICLE, { ...urlParams }) : ''}
        {...{ tileHover }}
      />
    </div>
  )
}

export const setContentLinksTargetAttribute = (content, value) => {
  const contentEl = (new DOMParser()).parseFromString(content, 'text/html').documentElement
  Array.prototype.slice.call(contentEl.querySelectorAll('a')).forEach((a) => {
    a.setAttribute('target', value)
    if (value === '_blank') {
      a.setAttribute('rel', 'noopener noreferrer')
    }
  })

  return contentEl
}

const Content = ({ headline = false, title, content, tileHover, aol }) => {
  const contentEl = setContentLinksTargetAttribute(content, '_blank')
  const titleText = htmlText(title)

  return (
    <div className={cx('ca-content black ca-homepage', { mt2: headline }, { mt4: !headline })}>
      <div
        className={cx(
          'b ca-tile-title',
          { underline: tileHover },
          { 'lh-headline': headline },
          { 'lh-title': !headline }
        )}
        style={{
          fontSize: headline ? '1.75rem' : '1.125rem'
        }}
      >
        {titleText}
      </div>
      <div
        style={{
          fontSize: 15,
          marginTop: '1.25rem',
          lineHeight: '1.47'
        }}
      >
        {parseHtmlContent(contentEl.innerHTML, { aol })}
      </div>
    </div>
  )
}

const PublishedDate = ({ publishDate }) => (
  <p className='f6 cadet-blue mt0 mb3'>
    {publishDate}
  </p>
)

export const HeadlineStory = ({ imageUrl = false, publishDate, title, content, id, path, aol, isLogged }) => {
  const [heightOverflowing, setHeightOverflowing] = useState(false)
  const [tileHover, setHover] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const urlParams = { aol, id: path[1] }
  const URL = isLogged ? url(CURRENT_AWARENESS_ARTICLE, { ...urlParams }) : ''

  const handleOnClick = useHandleArticleClick()

  return (
    <div
      className='ca-grid-card relative pt6 pb0 pl6 pr0 bg-white pointer'
      onClick={handleOnClick(isLogged, URL, aol)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Grid container className='h-100 pb9 relative'>
        <Grid item sm={imageUrl ? 6 : 12} md={imageUrl ? 4 : 12} className='relative h-100'>
          <TileOverFlowDetector
            headline
            {...{ publishDate, imageUrl, title, content, id, path, tileHover, setHeightOverflowing, aol }}
          />
          {
            heightOverflowing &&
              <div
                className={cx(
                  'absolute bottom0 tile-frosted-bottom w-100',
                  { h5: imageUrl },
                  { h5: !imageUrl })}
              />
          }
        </Grid>
        {imageUrl && (<HeadlineStoryImage {...{ imageUrl, imageLoaded, setImageLoaded }} />)}
        <Grid item sm={imageUrl ? 6 : 12} md={imageUrl ? 4 : 12} className='relative'>
          <div className='absolute bottom0 tile-frosted-bottom h3 w-100' />
        </Grid>
        {heightOverflowing &&
          <div className='absolute bottom6 left0'>
            <ReadFullStoryLink {...{ path, tileHover, aol, isLogged, onClick: handleOnClick(isLogged, URL, aol) }} />
          </div>}
      </Grid>
    </div>
  )
}

const HeadlineStoryImage = ({ imageUrl, setImageLoaded, imageLoaded }) => {
  const src = thumborRoute.reverse({ url: imageUrl, size: '640x360' })

  return (
    <Grid item sm={6} md={8} className='pl6 h-100'>
      {!imageLoaded && (
        <Skeleton variant='rect' animation='pulse' height={377} classes={{ root: 'bg-void-grey' }} />
      )}
      {!imageLoaded && <img src={src} className='dn' onLoad={() => setImageLoaded(true)} />}
      {imageLoaded && (
        <div
          style={{ backgroundImage: `url(${src})`, height: '377px' }}
          className={cx('fit-bg-image')}
        />
      )}
    </Grid>
  )
}

const TileOverFlowDetector = ({ headline, publishDate, imageUrl, title, content, tileHover, setHeightOverflowing, aol }) => {
  const { containerRef, contentRef, isOverflowing } = useOverflowDetection()

  useEffect(() => {
    setHeightOverflowing(isOverflowing)
  }, [isOverflowing, setHeightOverflowing])

  return (
    <div
      ref={containerRef}
      className='overflow-detector'
      style={{
        height: headline ? 'inherit' : !imageUrl ? 360 : 360 - 175,
        overflow: 'hidden'
      }}
    >
      <div ref={contentRef}>
        <Grid container direction='column' justifyContent='space-between' className={cx('relative h-100')}>
          <Grid item className='max-w-100'>
            <PublishedDate {...{ publishDate }} />
            <Content {...{ headline, imageUrl, title, content, tileHover, aol }} />
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export const Story = ({ imageUrl = false, publishDate, title, content, id, path, trimmed, aol, isLogged }) => {
  const [heightOverflowing, setHeightOverflowing] = useState(false)
  const [tileHover, setTileHover] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const urlParams = { aol, id: path[1] }
  const URL = isLogged ? url(CURRENT_AWARENESS_ARTICLE, { ...urlParams }) : ''

  const handleOnClick = useHandleArticleClick(isLogged, URL, aol)

  return (
    <div
      className='ca-grid-card relative pa6 bg-white pointer'
      onClick={handleOnClick(isLogged, URL, aol)}
      onMouseEnter={() => setTileHover(true)}
      onMouseLeave={() => setTileHover(false)}
    >
      <Grid container direction='column' justifyContent='space-between' className='h-100 relative'>
        <Grid item className='max-w-100'>
          <div className='relative'>
            <TileOverFlowDetector {...{ publishDate, imageUrl, title, content, id, path, tileHover, setHeightOverflowing, aol }} />
            {heightOverflowing && (
              <div className='absolute bottom0 tile-frosted-bottom w-100 h5' />
            )}
          </div>
          {imageUrl && (<StoryImage {...{ imageUrl, setImageLoaded, imageLoaded }} />)}
        </Grid>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <div className='absolute bottom0'>
            <ReadFullStoryLink {...{ path, tileHover, aol, isLogged, onClick: handleOnClick(isLogged, URL, aol) }} />
          </div>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          {(heightOverflowing || trimmed) &&
            <div className='absolute bottom0'>
              <ReadFullStoryLink {...{ path, tileHover, aol, isLogged, onClick: handleOnClick(isLogged, URL, aol) }} />
            </div>}
        </Box>
      </Grid>
    </div>
  )
}

const StoryImage = ({ imageUrl, setImageLoaded, imageLoaded }) => {
  const src = thumborRoute.reverse({ url: imageUrl, size: '320x200' })

  return (
    <Grid item className='pb6 pt4' style={{ maxHeight: 175 }}>
      {!imageLoaded && (
        <Skeleton variant='rect' animation='pulse' height={154} classes={{ root: 'bg-void-grey' }} />
      )}
      {!imageLoaded && <img src={src} className='dn' onLoad={() => setImageLoaded(true)} />}
      {imageLoaded && (
        <div
          className='fit-bg-image'
          style={{
            backgroundImage: `url(${src})`,
            height: '154px'
          }}
        />
      )}
    </Grid>
  )
}
